import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "historySection", "historyList", "historyChevron",
    "addendaModal", "levelingGrid", "videoCallBtn",
    "alternatesModal", "altCheckbox",
    "detailModal", "detailBackdrop", "detailPanel", "detailContent",
    "detailTitle", "editLink", "pdfLink", "deleteBtn",
    "prevBtn", "nextBtn", "deleteConfirmModal",
    "signWitnessModal", "signWitnessArea"
  ]

  static values = {
    projectId: Number,
    showAddendaModal: Boolean,
    delimiter: { type: String, default: "," },
    separator: { type: String, default: "." },
    bidIds: { type: Array, default: [] }
  }

  connect() {
    // Auto-show addenda modal if needed
    if (this.showAddendaModalValue && this.hasAddendaModalTarget) {
      this.addendaModalTarget.classList.remove("hidden")
    }

    this._modalOpen = false
    this._activeBidId = null
    this._onKeydown = this._onKeydown.bind(this)
    this._backdropTarget = null
  }

  // ── History log toggle ──────────────────────────────────────────

  toggleHistory() {
    if (!this.hasHistoryListTarget) return
    const hidden = this.historyListTarget.classList.toggle("hidden")
    if (this.hasHistoryChevronTarget) {
      this.historyChevronTarget.style.transform = hidden ? "" : "rotate(180deg)"
    }
  }

  // ── Addenda invalid modal ───────────────────────────────────────

  hideAddendaModal() {
    if (this.hasAddendaModalTarget) {
      this.addendaModalTarget.classList.add("hidden")
    }
  }

  // ── Alternates approval modal ───────────────────────────────────

  showAlternatesModal() {
    if (this.hasAlternatesModalTarget) {
      this.alternatesModalTarget.classList.remove("hidden")
    }
  }

  hideAlternatesModal() {
    if (this.hasAlternatesModalTarget) {
      this.alternatesModalTarget.classList.add("hidden")
    }
  }

  async saveAlternates(event) {
    const url = event.currentTarget.dataset.url
    const indices = this.altCheckboxTargets
      .filter(cb => cb.checked)
      .map(cb => parseInt(cb.dataset.index, 10))

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
      body: JSON.stringify({ alternates_arr: indices })
    })

    if (response.ok) {
      this.hideAlternatesModal()
      // Refresh table via Turbo visit (smooth swap, no full page reload)
      if (window.Turbo) {
        Turbo.visit(window.location.href, { action: "replace" })
      }
    }
  }

  // ── Bid leveling export ─────────────────────────────────────────

  exportBidLeveling(event) {
    event.preventDefault()
    const format = event.currentTarget.dataset.format || "pdf"
    window.open(`/bids/${this.projectIdValue}/bid_leveling_export.${format}`, "_blank")
  }

  // ── Bid Detail Drawer ───────────────────────────────────────────

  openBid(event) {
    // Don't open if clicking a link inside the row
    if (event.target.closest("a")) return

    const row = event.currentTarget.closest("tr")
    const bidId = parseInt(row.dataset.bidId, 10)
    if (bidId) this._openDetail(bidId)
  }

  closeDetail() {
    this._closeModal()
  }

  backdropMousedown(event) {
    this._backdropTarget = event.target
  }

  closeOnBackdrop(event) {
    if (event.target === this._backdropTarget) {
      this._closeModal()
    }
    this._backdropTarget = null
  }

  prevBid() {
    const ids = this.bidIdsValue
    const idx = ids.indexOf(this._activeBidId)
    if (idx > 0) this._openDetail(ids[idx - 1])
  }

  nextBid() {
    const ids = this.bidIdsValue
    const idx = ids.indexOf(this._activeBidId)
    if (idx >= 0 && idx < ids.length - 1) this._openDetail(ids[idx + 1])
  }

  // ── Delete flow ─────────────────────────────────────────────────

  showDeleteConfirm() {
    if (this.hasDeleteConfirmModalTarget) {
      this.deleteConfirmModalTarget.classList.remove("hidden")
    }
  }

  cancelDelete() {
    if (this.hasDeleteConfirmModalTarget) {
      this.deleteConfirmModalTarget.classList.add("hidden")
    }
  }

  async confirmDelete() {
    if (!this._activeBidId) return

    const url = `/projects/${this.projectIdValue}/bids/${this._activeBidId}`
    try {
      const resp = await fetch(url, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (resp.ok) {
        // Remove the row from the table
        const row = this.element.querySelector(`tr[data-bid-id="${this._activeBidId}"]`)
        if (row) row.remove()

        // Remove from bidIds
        const ids = this.bidIdsValue.filter(id => id !== this._activeBidId)
        this.bidIdsValue = ids

        this.cancelDelete()
        this._closeModal()
      }
    } catch (e) {
      // Silently handle — user can retry
    }
  }

  // ── Sign Witness ───────────────────────────────────────────────

  showSignWitnessModal() {
    if (this.hasSignWitnessModalTarget) {
      this.signWitnessModalTarget.classList.remove("hidden")
    }
  }

  hideSignWitnessModal() {
    if (this.hasSignWitnessModalTarget) {
      this.signWitnessModalTarget.classList.add("hidden")
    }
  }

  async confirmSignWitness(event) {
    const btn = event.currentTarget
    const url = btn.dataset.url
    const witnessId = btn.dataset.witnessId
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const resp = await fetch(`${url}?witness=${witnessId}`, {
        headers: { "Accept": "application/json", "X-CSRF-Token": token }
      })
      const data = await resp.json()

      if (data.message === "signed") {
        // Update button area to signed state
        const count = parseInt(this.signWitnessAreaTarget.dataset.witnessesCount || "0", 10) + 1
        this.signWitnessAreaTarget.innerHTML = `
          <span class="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-gray-400 select-none">
            ✓ Signed ${count}/2
          </span>
        `
        // Remove the modal from the DOM
        this.hideSignWitnessModal()
        this.signWitnessModalTarget.remove()
      }
    } catch (e) {
      // Silently handle — user can retry
    }
  }

  // ── Private ─────────────────────────────────────────────────────

  async _openDetail(bidId) {
    this._activeBidId = bidId
    this._modalOpen = true

    // Show modal
    this.detailModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"

    // Skeleton loading
    this.detailContentTarget.innerHTML = `
      <div class="p-5 space-y-4">
        <div class="h-4 bg-gray-100 rounded w-2/3 animate-pulse"></div>
        <div class="h-4 bg-gray-100 rounded w-1/2 animate-pulse"></div>
        <div class="h-24 bg-gray-100 rounded animate-pulse"></div>
        <div class="h-4 bg-gray-100 rounded w-3/4 animate-pulse"></div>
      </div>
    `

    // Animate in
    requestAnimationFrame(() => {
      this.detailBackdropTarget.classList.remove("opacity-0")
      this.detailBackdropTarget.classList.add("opacity-100")
      this.detailPanelTarget.classList.remove("translate-x-full")
      this.detailPanelTarget.classList.add("translate-x-0")
    })

    // Highlight row
    this._highlightRow(bidId)
    this._updateNavButtons()

    // Add keyboard listener
    document.addEventListener("keydown", this._onKeydown)

    // Fetch detail HTML
    const url = `/projects/${this.projectIdValue}/bids/${bidId}`
    try {
      const resp = await fetch(url, { headers: { "Accept": "text/html" } })
      if (!resp.ok) throw new Error(resp.status)
      const html = await resp.text()

      // Check if still showing this bid (user may have navigated)
      if (this._activeBidId !== bidId) return

      this.detailContentTarget.innerHTML = html

      // Extract permissions
      const perms = this.detailContentTarget.querySelector("#bid-permissions")
      const canEdit = perms?.dataset.canEdit === "true"
      const canDelete = perms?.dataset.canDelete === "true"

      // Extract header info
      const headerTpl = this.detailContentTarget.querySelector("#bid-header-info")
      if (headerTpl && this.hasDetailTitleTarget) {
        this.detailTitleTarget.innerHTML = ""
        this.detailTitleTarget.appendChild(headerTpl.content.cloneNode(true))
      }

      // Set button visibility and hrefs
      if (this.hasEditLinkTarget) {
        if (canEdit) {
          this.editLinkTarget.classList.remove("hidden")
          this.editLinkTarget.classList.add("inline-flex")
          this.editLinkTarget.href = `/projects/${this.projectIdValue}/bids/${bidId}/edit`
        } else {
          this.editLinkTarget.classList.add("hidden")
          this.editLinkTarget.classList.remove("inline-flex")
        }
      }

      if (this.hasPdfLinkTarget) {
        this.pdfLinkTarget.href = `/bids/${this.projectIdValue}/bid_detail/${bidId}`
      }

      if (this.hasDeleteBtnTarget) {
        if (canDelete) {
          this.deleteBtnTarget.classList.remove("hidden")
          this.deleteBtnTarget.classList.add("inline-flex")
        } else {
          this.deleteBtnTarget.classList.add("hidden")
          this.deleteBtnTarget.classList.remove("inline-flex")
        }
      }
    } catch (e) {
      if (this._activeBidId === bidId) {
        this.detailContentTarget.innerHTML = `
          <div class="flex flex-col items-center justify-center h-full text-gray-400">
            <p>Failed to load bid details.</p>
          </div>
        `
      }
    }
  }

  _closeModal() {
    if (!this._modalOpen) return
    this._modalOpen = false
    this._activeBidId = null

    document.body.style.overflow = ""
    document.removeEventListener("keydown", this._onKeydown)

    // Animate out
    this.detailBackdropTarget.classList.remove("opacity-100")
    this.detailBackdropTarget.classList.add("opacity-0")
    this.detailPanelTarget.classList.remove("translate-x-0")
    this.detailPanelTarget.classList.add("translate-x-full")

    // Hide after animation
    const handler = () => {
      this.detailModalTarget.classList.add("hidden")
      this.detailPanelTarget.removeEventListener("transitionend", handler)
    }
    this.detailPanelTarget.addEventListener("transitionend", handler, { once: true })

    // Remove row highlight
    this.element.querySelectorAll("tr[data-bid-id]").forEach(r => r.classList.remove("bg-brand/5"))

    // Hide delete confirm if open
    this.cancelDelete()
  }

  _highlightRow(bidId) {
    this.element.querySelectorAll("tr[data-bid-id]").forEach(r => {
      r.classList.toggle("bg-brand/5", parseInt(r.dataset.bidId, 10) === bidId)
    })
  }

  _updateNavButtons() {
    const ids = this.bidIdsValue
    const idx = ids.indexOf(this._activeBidId)
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = idx <= 0
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = idx < 0 || idx >= ids.length - 1
  }

  _onKeydown(event) {
    if (!this._modalOpen) return

    if (event.key === "Escape") {
      // If delete confirm is open, close that first
      if (this.hasDeleteConfirmModalTarget && !this.deleteConfirmModalTarget.classList.contains("hidden")) {
        this.cancelDelete()
      } else {
        this._closeModal()
      }
    } else if (event.key === "ArrowLeft") {
      this.prevBid()
    } else if (event.key === "ArrowRight") {
      this.nextBid()
    }
  }
}
